<script setup lang="ts">
// Modèles & évènements
import type { UserDataTableNamespace } from '@/presentation/components/users/utils/UserDataTableType.ts'
import type { UserSearchRequestEntity } from '@/core/infrastructure/schemas/users/core/UserInputSchema.ts'
import CHideText from '@/presentation/components/base/CHideText.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import CUserFiltersPanel from '@/presentation/components/users/CUserFiltersPanel.vue'
import { ref } from 'vue'
import PMenu from 'primevue/menu'
import type { MenuItem as PMenuItem } from 'primevue/menuitem'
import PButton from 'primevue/button'
import PColumn from 'primevue/column'
import { type DataTableSortEvent } from 'primevue/datatable'
import PDataTable from 'primevue/datatable'
import PSkeleton from 'primevue/skeleton'
import PAvatar from 'primevue/avatar'
import { useDebounceFn } from '@vueuse/core'

// Modèles
const buffer = defineModel<Array<UserDataTableNamespace.UserDataTableRow | undefined>>('buffer', {
  required: true
})
const filters = defineModel<UserSearchRequestEntity>('filters', { required: true })

// Propriétés
const props = defineProps<{
  totalRecords: number
  applyFilters: () => void
  filtersLoading: boolean
  onSort: (event: DataTableSortEvent) => void
  onLazyLoading: (event: UserDataTableNamespace.UserDataTableLazyEvent) => void
}>()

// Refs
const rowMenu = ref()
const currentMenuItems = ref<PMenuItem[]>([])

// Fonctions
/**
 * Convertit les actions en menu items
 * @param user
 */
function toMenuItems(user: UserDataTableNamespace.UserDataTableRow): PMenuItem[] {
  const entries = Object.values(user.actions) as UserDataTableNamespace.ActionItem[]
  return entries
    .filter((a) => !a.hidden) // on ne garde pas les actions cachées
    .map((a) => ({
      label: a.label,
      icon: a.icon,
      disabled: a.disabled,
      command: () => a.handler(user)
    }))
}

/**
 * Déclencheur d'ouverture de menu
 * @param event
 * @param row
 */
function openRowMenu(event: MouseEvent, row: UserDataTableNamespace.UserDataTableRow) {
  currentMenuItems.value = toMenuItems(row)
  rowMenu.value.toggle(event)
}
const onLazyLoadDebounced = useDebounceFn(
  (e: UserDataTableNamespace.UserDataTableLazyEvent) => props.onLazyLoading(e),
  1000
)
</script>

<template>
  <p-data-table
    :value="buffer"
    dataKey="identifier"
    :scrollable="true"
    scrollHeight="flex"
    stripedRows
    sortMode="multiple"
    removable-sort
    :totalRecords="totalRecords"
    :loading="false"
    stateStorage="session"
    @sort="onSort"
    :virtualScrollerOptions="{
      itemSize: 65,
      numToleratedItems: 10,
      lazy: true,
      showLoader: true,
      onLazyLoad: onLazyLoadDebounced
    }"
  >
    <template #header>
      <div class="col-span-12 grid grid-cols-12 items-center justify-start gap-4">
        <c-user-filters-panel
          :filters="filters"
          :filters-loading="props.filtersLoading"
          @submit="props.applyFilters"
        />
      </div>
    </template>

    <p-column header="" class="hidden md:table-cell">
      <template #body="{ data }">
        <template v-if="data">
          <p-avatar :image="data.picture" class="mr-2" shape="circle" />
        </template>
        <template v-else>
          <p-skeleton width="120px" height="1rem" />
        </template>
      </template>
    </p-column>

    <p-column header="Prénom" field="firstname" :sortable="true">
      <template #body="{ data }">
        <template v-if="data">
          <span data-maskable>{{ data.firstname }}</span>
        </template>
        <template v-else>
          <p-skeleton width="8rem" height="1rem" />
        </template>
      </template>
    </p-column>

    <p-column header="Nom" field="lastname" :sortable="true">
      <template #body="{ data }">
        <template v-if="data">
          <span data-maskable>{{ data.lastname }}</span>
        </template>
        <template v-else>
          <p-skeleton width="10rem" height="1rem" />
        </template>
      </template>
    </p-column>

    <p-column header="Rôles">
      <template #body="{ data }">
        <template v-if="data">
          <ul class="list-none" v-for="role in data.roles.second">
            <li :key="`${data.identifier}_${role.first}`">{{ role }}</li>
          </ul>
        </template>
        <template v-else>
          <p-skeleton width="6rem" height="1rem" />
        </template>
      </template>
    </p-column>

    <p-column header="Email">
      <template #body="{ data }">
        <template v-if="data">
          <div class="w-60">
            <c-hide-text :text="data.email" label="Afficher l'email" :duration="5" data-maskable />
          </div>
        </template>
        <template v-else>
          <p-skeleton width="6rem" height="1rem" />
        </template>
      </template>
    </p-column>

    <p-column header="Username">
      <template #body="{ data }">
        <template v-if="data">
          <div class="w-60">
            <template v-if="data.username">
              <c-hide-text
                :text="data.username"
                label="Afficher le nom utilisateur"
                data-maskable
                :duration="5"
              />
            </template>
            <template v-else>
              <p>-</p>
            </template>
          </div>
        </template>
        <template v-else>
          <p-skeleton width="6rem" height="1rem" />
        </template>
      </template>
    </p-column>

    <p-column header="Genre">
      <template #body="{ data }">
        <template v-if="data">
          {{ data.gender.second }}
        </template>
        <template v-else>
          <p-skeleton width="6rem" height="1rem" />
        </template>
      </template>
    </p-column>

    <p-column header="Bloqué">
      <template #body="{ data }">
        <template v-if="data">
          <font-awesome-icon
            :icon="data.blocked ? 'lock' : 'lock-open'"
            :class="data.blocked ? 'text-red-500' : 'text-green-500'"
          />
        </template>
        <template v-else>
          <p-skeleton width="6rem" height="1rem" />
        </template>
      </template>
    </p-column>

    <p-column header="Email vérifié">
      <template #body="{ data }">
        <template v-if="data">
          <font-awesome-icon
            size="lg"
            :icon="data.emailVerified ? 'check' : 'xmark'"
            :class="data.emailVerified ? 'text-green-500' : 'text-red-500'"
          />
        </template>
        <template v-else>
          <p-skeleton width="6rem" height="1rem" />
        </template>
      </template>
    </p-column>

    <p-column header="Dernière connexion">
      <template #body="{ data }">
        <template v-if="data">
          <p class="text-center>">{{ data.lastLogin }}</p>
        </template>
        <template v-else>
          <p-skeleton width="6rem" height="1rem" />
        </template>
      </template>
    </p-column>

    <p-column header="Nombre de connexion">
      <template #body="{ data }">
        <template v-if="data">
          <p class="text-right slashed-zero tabular-nums">{{ data.loginCount }}</p>
        </template>
        <template v-else>
          <p-skeleton width="6rem" height="1rem" />
        </template>
      </template>
    </p-column>

    <p-column header="Actions">
      <template #body="{ data }">
        <template v-if="data">
          <p-button icon="pi pi-ellipsis-v" @click="(e) => openRowMenu(e, data)" text rounded />
          <p-menu ref="rowMenu" :model="currentMenuItems" :popup="true" />
        </template>
        <template v-else>
          <p-skeleton width="6rem" height="1rem" />
        </template>
      </template>
    </p-column>
  </p-data-table>
</template>